const memoryCache = new Map();
const inflightRequests = new Map();

/** Session + TTL cache only in production builds (disabled for `npm start`). */
const CACHE_ENABLED = process.env.NODE_ENV === "production";

/** Baked in at build time (GitHub commit SHA). Changes on each production deploy. */
const BUILD_ID = process.env.REACT_APP_BUILD_ID || "local-dev";

const CACHE_PREFIX = "portfolio:";

function isEntryValid(entry) {
  if (!entry || typeof entry !== "object") {
    return false;
  }
  if (entry.buildId !== BUILD_ID) {
    return false;
  }
  return Date.now() < entry.expiresAt;
}

function removeEntry(key) {
  memoryCache.delete(key);
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/** Drop cached API data from older deployments (production only). */
function purgeStaleCache() {
  if (!CACHE_ENABLED) {
    memoryCache.clear();
    try {
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(CACHE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch {
      // sessionStorage unavailable
    }
    return;
  }

  memoryCache.forEach((entry, key) => {
    if (!isEntryValid(entry)) {
      memoryCache.delete(key);
    }
  });

  try {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (!key?.startsWith(CACHE_PREFIX)) {
        continue;
      }
      try {
        const entry = JSON.parse(sessionStorage.getItem(key));
        if (!isEntryValid(entry)) {
          keysToRemove.push(key);
        }
      } catch {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(removeEntry);
  } catch {
    // sessionStorage unavailable
  }
}

purgeStaleCache();

function readEntry(key) {
  if (!CACHE_ENABLED) {
    return null;
  }

  const fromMemory = memoryCache.get(key);
  if (fromMemory) {
    if (isEntryValid(fromMemory)) {
      return fromMemory.value;
    }
    memoryCache.delete(key);
  }

  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }

    const entry = JSON.parse(raw);
    if (!isEntryValid(entry)) {
      removeEntry(key);
      return null;
    }

    memoryCache.set(key, entry);
    return entry.value;
  } catch {
    return null;
  }
}

function writeEntry(key, value, ttlMs) {
  if (!CACHE_ENABLED) {
    return;
  }

  const entry = {
    value,
    buildId: BUILD_ID,
    expiresAt: Date.now() + ttlMs,
  };

  memoryCache.set(key, entry);

  try {
    sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // sessionStorage full or unavailable — memory cache still applies this session
  }
}

export function getCached(cacheKey) {
  return readEntry(cacheKey);
}

export function invalidateCache(cacheKey) {
  removeEntry(cacheKey);
}

/**
 * Fetch JSON once, then reuse from memory + sessionStorage until TTL expires
 * or a new deployment changes REACT_APP_BUILD_ID. Caching is production-only;
 * local dev always hits the network (in-flight deduplication still applies).
 * @param {object} [options]
 * @param {(data: unknown) => boolean} [options.shouldCache] - When false, response is not stored (e.g. email: null).
 */
export async function fetchCachedJson(url, cacheKey, ttlMs, options = {}) {
  const shouldCache = options.shouldCache ?? (() => true);

  const cached = readEntry(cacheKey);
  if (cached !== null) {
    return cached;
  }

  if (inflightRequests.has(cacheKey)) {
    return inflightRequests.get(cacheKey);
  }

  const request = (async () => {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }

    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
      throw new Error("Expected JSON response.");
    }

    const data = await response.json();
    if (shouldCache(data)) {
      writeEntry(cacheKey, data, ttlMs);
    }
    return data;
  })();

  inflightRequests.set(cacheKey, request);

  try {
    return await request;
  } finally {
    inflightRequests.delete(cacheKey);
  }
}

export const CACHE_KEYS = {
  portfolio: `${CACHE_PREFIX}portfolio-data`,
  contact: `${CACHE_PREFIX}contact-info`,
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export const CACHE_TTL = {
  portfolio: ONE_DAY_MS,
  contact: ONE_DAY_MS,
};
